/**
 * Choix de l'adresse de livraison d'un client (caisse et tableau de bord).
 *
 * Une adresse ajoutée ici est géocodée à l'enregistrement : l'état affiché dit
 * au préparateur si le livreur sera guidé jusqu'au numéro, jusqu'à la rue, ou
 * pas du tout.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';

export interface Adresse {
  id: string;
  street: string;
  postal_code: string;
  city: string;
  latitude?: number | null;
  longitude?: number | null;
  geocode_precision?: string | null;
}

export const adresseLisible = (a: Adresse) => `${a.street}, ${a.postal_code} ${a.city}`;

function etatGeocodage(a: Adresse): { texte: string; classe: string } {
  if (a.latitude == null || a.longitude == null) {
    return { texte: 'Adresse non localisée — le livreur suivra l’adresse écrite', classe: 'text-amber-700' };
  }
  if (a.geocode_precision === 'street' || a.geocode_precision === 'locality') {
    return { texte: 'Localisée à la rue seulement — vérifier le numéro sur place', classe: 'text-amber-700' };
  }
  return { texte: 'Adresse localisée ✓', classe: 'text-emerald-700' };
}

interface Props {
  customerId: string;
  value: Adresse | null;
  onChange: (a: Adresse | null) => void;
  /** Adresse à présélectionner si le client l'a déjà (commande en ligne). */
  preferee?: string | null;
}

export default function AdresseLivraison({ customerId, value, onChange, preferee }: Props) {
  const [adresses, setAdresses] = useState<Adresse[]>([]);
  const [ajout, setAjout] = useState(false);
  const [saisie, setSaisie] = useState({ street: '', postal_code: '', city: '' });
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    let annule = false;
    api.get(`/customers/${customerId}/addresses`)
      .then((r: any) => {
        if (annule) return;
        const liste = ((r?.data || r || []) as Adresse[]);
        setAdresses(liste);
        onChange(liste.find((a) => a.id === preferee) ?? liste[0] ?? null);
        setAjout(liste.length === 0);
      })
      .catch((e: any) => { logger.error('[Livraison] adresses', e); setAjout(true); });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const enregistrer = async () => {
    const { street, postal_code, city } = saisie;
    if (!street.trim() || !postal_code.trim() || !city.trim()) {
      toast.error('Rue et numéro, code postal et localité');
      return;
    }
    setEnvoi(true);
    try {
      const r: any = await api.post(`/customers/${customerId}/addresses`, {
        type: 'delivery', street: street.trim(), postal_code: postal_code.trim(), city: city.trim(),
        // Un code postal à 5 chiffres est français (Nord) ; le géocodage change de source.
        country: postal_code.trim().length === 5 ? 'FR' : 'BE', is_default: adresses.length === 0,
      });
      const adresse = (r?.data || r) as Adresse;
      setAdresses((l) => [...l, adresse]);
      onChange(adresse);
      setAjout(false);
      setSaisie({ street: '', postal_code: '', city: '' });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Adresse non enregistrée');
    } finally {
      setEnvoi(false);
    }
  };

  const champ = 'mt-1 w-full border border-gray-300 rounded-lg px-3 py-3 text-lg';

  return (
    <div>
      <div className="space-y-2">
        {adresses.map((a) => {
          const etat = etatGeocodage(a);
          return (
            <label key={a.id} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer ${value?.id === a.id ? 'border-sky-600 bg-sky-50' : 'border-gray-200'}`}>
              <input type="radio" name="liv-adresse" checked={value?.id === a.id} onChange={() => onChange(a)} className="mt-1" />
              <span>
                <span className="block font-medium">{adresseLisible(a)}</span>
                <span className={`block text-sm ${etat.classe}`}>{etat.texte}</span>
              </span>
            </label>
          );
        })}
      </div>
      {ajout ? (
        <div className="mt-2 grid grid-cols-3 gap-3">
          <label className="col-span-3 block"><span className="text-sm text-gray-600">Rue et numéro</span>
            <input id="liv-rue" value={saisie.street} onChange={(e) => setSaisie({ ...saisie, street: e.target.value })} className={champ} /></label>
          <label className="block"><span className="text-sm text-gray-600">Code postal</span>
            <input id="liv-cp" inputMode="numeric" value={saisie.postal_code} onChange={(e) => setSaisie({ ...saisie, postal_code: e.target.value })} className={champ} /></label>
          <label className="col-span-2 block"><span className="text-sm text-gray-600">Localité</span>
            <input id="liv-ville" value={saisie.city} onChange={(e) => setSaisie({ ...saisie, city: e.target.value })} className={champ} /></label>
          <button type="button" onClick={enregistrer} disabled={envoi} className="col-span-3 py-3 rounded-xl bg-sky-700 text-white font-bold disabled:opacity-50">
            {envoi ? 'Localisation…' : 'Enregistrer l’adresse'}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setAjout(true)} className="mt-2 text-sky-700 font-semibold">+ Autre adresse</button>
      )}
    </div>
  );
}
